/**
 * src/services/teleportReverseGeocode.ts
 *
 * Reverse-geocode a (lat, lng) into a human-readable place name for
 * the AI-agent teleport HUD's "Arrived" + "Next teleport" badges.
 *
 * Cache strategy
 * --------------
 *   1. In-memory LRU keyed by rounded coordinates ("lat,lng" to 4 dp,
 *      ~11 m precision) — agent positions inside the same block share
 *      the same cache row, so a flying-around fleet doesn't re-pay
 *      Mapbox per stop.
 *   2. Per-key in-flight de-dupe Promise so simultaneous calls
 *      (rotation tick + countdown peek hitting the same coord at
 *      almost the same instant) only fire ONE Mapbox request.
 *
 * We deliberately do NOT involve GeoLink's geocoding cache here —
 * that cache is keyed by *forward* query ("times square") + country,
 * and storing a reverse hit there would pollute its forward
 * lookup semantics. Cheap in-memory cache is fine for HUD labels.
 *
 * Failure mode: returns `null`, never throws. Callers should treat
 * "no name available" as a soft state (show coords or "Unknown
 * place") rather than treating absence as an error.
 */

import mapboxgl from 'mapbox-gl';

interface CachedName {
  /**
   * Primary display label — typically the most-specific level we
   * resolved (neighborhood / locality / place). Example: "Times
   * Square" or "Manhattan" or "New York".
   */
  short:   string;
  /**
   * Region (state / province) short_code WITHOUT the country prefix
   * — e.g. "NY", "CA", "ON". Empty string when Mapbox didn't return
   * a region (some countries / smaller features). The HUD uses this
   * as the FIRST half of the secondary line.
   */
  region:  string;
  /**
   * Country two-letter ISO 3166-1 alpha-2 — e.g. "US", "JP". Empty
   * string when not resolved. The HUD uses this as the SECOND half
   * of the secondary line.
   */
  country: string;
  /**
   * Long-form, the full place_name returned by Mapbox. Useful as a
   * tooltip / accessibility label so the user can see the full
   * hierarchy ("Times Square, Manhattan, New York, New York, USA")
   * without us having to format it ourselves.
   */
  long:    string;
  /** When this entry was inserted (Date.now()) — drives LRU eviction. */
  ts:      number;
}

/**
 * Pull the region (state) + country short codes out of a Mapbox
 * geocoding feature's `context` array. Mapbox formats the region's
 * `short_code` as e.g. "US-NY" — we strip the country prefix so the
 * HUD shows "NY" not "US-NY". Returns empty strings for missing
 * fields rather than `null` so consumers don't have to null-check.
 */
function _extractContext(feat: any): { region: string; country: string } {
  const ctx = Array.isArray(feat?.context) ? feat.context : [];
  let region  = '';
  let country = '';
  for (const c of ctx) {
    const id = String(c?.id || '');
    if (!region && id.startsWith('region.')) {
      const sc = String(c.short_code || '');
      // "US-NY" → "NY"; bare "NY" stays "NY"; missing short_code →
      // fall back to text ("New York"). The split-pop trick is
      // safe because short_code is either "XX" or "XX-YY".
      region = sc.includes('-') ? sc.split('-').pop() || '' : (sc || c.text || '');
    } else if (!country && id.startsWith('country.')) {
      country = String(c.short_code || '').toUpperCase();
    }
  }
  return { region, country };
}

const CACHE_MAX = 500;
const ROUND_DP  = 4;       // ~11 m precision; same coord block hits the same row

const _cache    = new Map<string, CachedName>();
const _inflight = new Map<string, Promise<CachedName | null>>();

function _key(lat: number, lng: number): string {
  return `${lat.toFixed(ROUND_DP)},${lng.toFixed(ROUND_DP)}`;
}

function _evictIfNeeded() {
  if (_cache.size <= CACHE_MAX) return;
  // Find the oldest entry (smallest ts) and drop it. We don't bother
  // with a true LRU — at 500 entries this is microseconds and the HUD
  // only ever asks for a few unique places per session.
  // Array.from() so the iterator type works under the project's
  // ES5 lib target without --downlevelIteration.
  let oldestKey: string | null = null;
  let oldestTs = Infinity;
  for (const [k, v] of Array.from(_cache.entries())) {
    if (v.ts < oldestTs) {
      oldestTs = v.ts;
      oldestKey = k;
    }
  }
  if (oldestKey) _cache.delete(oldestKey);
}

/**
 * Get the cached name immediately if we have it, otherwise fire the
 * lookup in the background and return null. Useful for the HUD's
 * synchronous render path — show coords now, get the name on the
 * next render once the fetch resolves.
 *
 * To force-await the lookup (e.g. to await a place name before
 * publishing a phase frame), call `reverseGeocodeLatLng()` directly.
 */
export function getCachedReverseName(lat: number, lng: number): CachedName | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return _cache.get(_key(lat, lng)) ?? null;
}

/**
 * Resolve a place name for (lat, lng). Returns null on any failure.
 * Caches successful results for the lifetime of the page.
 *
 * NOTE: an `AbortController.signal` is intentionally NOT supported —
 * we want the underlying fetch to RUN and populate the cache even if
 * the calling component unmounted, so the next mount finds the row
 * pre-warmed. Cancellation here would just waste an in-flight quota.
 */
export async function reverseGeocodeLatLng(
  lat: number,
  lng: number,
): Promise<CachedName | null> {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const key = _key(lat, lng);
  const hit = _cache.get(key);
  if (hit) return hit;
  const inFlight = _inflight.get(key);
  if (inFlight) return inFlight;

  const token = mapboxgl.accessToken || (process.env.REACT_APP_MAPBOX_TOKEN || '');
  if (!token) return null;

  const work = (async () => {
    try {
      const url = new URL(`https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json`);
      url.searchParams.set('access_token', token);
      url.searchParams.set('limit', '1');
      // Prefer place / locality / neighborhood for human-friendly
      // labels — POI-level reverse hits are too narrow for a HUD
      // ("McDonald's #4523" isn't useful when the agent is walking
      // past).
      url.searchParams.set('types', 'neighborhood,locality,place,district');
      const r = await fetch(url.toString());
      if (!r.ok) return null;
      const j = await r.json();
      const feat = Array.isArray(j?.features) && j.features.length > 0 ? j.features[0] : null;
      if (!feat) return null;
      const ctx = _extractContext(feat);
      const name: CachedName = {
        short:   feat.text || feat.place_name || `${lat.toFixed(3)}, ${lng.toFixed(3)}`,
        region:  ctx.region,
        country: ctx.country,
        long:    feat.place_name || feat.text || `${lat.toFixed(3)}, ${lng.toFixed(3)}`,
        ts:      Date.now(),
      };
      _cache.set(key, name);
      _evictIfNeeded();
      return name;
    } catch {
      return null;
    } finally {
      _inflight.delete(key);
    }
  })();
  _inflight.set(key, work);
  return work;
}

/**
 * Pre-cache a place name from a known forward-geocode result (e.g.
 * the user typed "times square" and we already got the place_name
 * back from the search service). Avoids a duplicate Mapbox call when
 * the cinematic later flies to that stop and wants to show the same
 * name in the "Arrived" badge.
 */
export function primeReverseCache(lat: number, lng: number, placeName: string): void {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
  if (!placeName) return;
  const key = _key(lat, lng);
  // Don't overwrite an existing entry — Mapbox's reverse-geocoding
  // result is usually richer than the forward result (it includes
  // structured context with explicit region/country fields).
  if (_cache.has(key)) return;
  // Best-effort split of "Times Square, Manhattan, New York, USA"
  // into [primary, region, country] using the comma list. The last
  // segment is usually the country name (we don't have a short code
  // here), the second-to-last is usually the region, and the first
  // is the most specific. This is intentionally approximate — when
  // the cinematic later flies to this stop, the reverse geocode
  // will overwrite this with the structured Mapbox response (we
  // explicitly DON'T overwrite when an entry already exists, so
  // primed-then-resolved is fine, but resolved-then-primed wouldn't
  // happen because primeReverseCache short-circuits above).
  const parts = placeName.split(',').map((s) => s.trim()).filter(Boolean);
  const short   = parts[0] || placeName;
  const country = parts.length >= 3 ? (parts[parts.length - 1] || '') : '';
  const region  = parts.length >= 3 ? (parts[parts.length - 2] || '') : '';
  _cache.set(key, {
    short,
    region,
    country,
    long: placeName,
    ts:   Date.now(),
  });
  _evictIfNeeded();
}
